import React, { useEffect, useMemo, useState } from 'react'
import {
  Btn,
  CustomCalendar,
  CustomCheckbox,
  CustomDropdown,
  CustomInputTextArea,
  CustomModal,
  InputText,
  InputTime,
} from '../../../../components'
import * as colors from '../../../../assets/js/colors'
import { getEmpresas } from '../../../../services/localstorage/localstorage.service'
import {
  formatarDataHora,
  formatarDataHoraPayload,
  parseDataHora,
} from '../ChecklistForm/respostas.util'

const LIMITES = { responsavelNome: 255, observacao: 2000 }

const styles = {
  checkbox: { display: 'flex', alignItems: 'flex-end', paddingBottom: 15 },
  dica: { color: colors.gray, fontSize: '0.8rem', marginTop: 5 },
}

const pad = valor => `${valor}`.padStart(2, '0')

function formularioVazio() {
  return {
    checklist: null,
    company: null,
    responsavelUser: null,
    semUsuario: false,
    responsavelNome: '',
    comDeadline: false,
    deadlineData: null,
    deadlineHora: '',
    observacao: '',
  }
}

function formularioDe(preenchimento, formInit, empresas) {
  if (!preenchimento) return formularioVazio()

  const deadline = parseDataHora(preenchimento.deadline)
  const checklist = formInit.checklists.find(c => c.id === preenchimento.checklist_id) || {
    id: preenchimento.checklist_id,
    checklist_code: preenchimento.checklist_code,
    title: preenchimento.title,
  }
  const company = preenchimento.company_id
    ? empresas.find(c => c.id === preenchimento.company_id) || {
        id: preenchimento.company_id,
        name: preenchimento.company_name,
      }
    : null
  const responsavelUser = preenchimento.responsavel_user_id
    ? formInit.users.find(u => u.id === preenchimento.responsavel_user_id) || {
        id: preenchimento.responsavel_user_id,
        name: preenchimento.responsavel_user_name,
      }
    : null

  return {
    checklist,
    company,
    responsavelUser,
    semUsuario: !responsavelUser,
    responsavelNome: preenchimento.responsavel_nome || '',
    comDeadline: !!deadline,
    deadlineData: deadline,
    deadlineHora: deadline ? `${pad(deadline.getHours())}:${pad(deadline.getMinutes())}` : '',
    observacao: preenchimento.observacao || '',
  }
}

function incluirOpcao(opcoes, opcao) {
  if (!opcao || opcoes.some(o => o.id === opcao.id)) return opcoes
  return [opcao, ...opcoes]
}

function validar(form, preenchimento) {
  if (!form.checklist) return 'Selecione o formulário'

  if (form.semUsuario) {
    if (!form.responsavelNome.trim()) return 'Informe o nome do responsável pelo preenchimento'
  } else if (!form.responsavelUser) {
    return 'Selecione o responsável pelo preenchimento'
  }

  if (form.comDeadline) {
    const deadline = formatarDataHoraPayload(form.deadlineData, form.deadlineHora)
    if (!deadline) return 'Informe a data e a hora do deadline'

    const limite = preenchimento ? parseDataHora(preenchimento.created_at) : new Date()
    const referencia = new Date(
      limite.getFullYear(),
      limite.getMonth(),
      limite.getDate(),
      limite.getHours(),
      limite.getMinutes(),
    )
    if (parseDataHora(deadline) < referencia) {
      return preenchimento
        ? 'O deadline não pode ser anterior à criação do preenchimento'
        : 'O deadline não pode ser anterior à data e hora atuais'
    }
  }

  if (form.observacao.length > LIMITES.observacao) {
    return `A observação deve ter no máximo ${LIMITES.observacao} caracteres`
  }

  return null
}

export default function ModalPreenchimento({
  show,
  preenchimento,
  formInit,
  onClose,
  onSave,
  onError,
}) {
  const [form, setForm] = useState(formularioVazio)
  const empresas = useMemo(() => getEmpresas(), [])

  useEffect(() => {
    if (show) setForm(formularioDe(preenchimento, formInit, empresas))
  }, [show, preenchimento, formInit])

  const edicao = !!preenchimento
  const onChange = (chave, valor) => setForm(prev => ({ ...prev, [chave]: valor }))

  const checklists = incluirOpcao(formInit.checklists, form.checklist).map(c => ({
    ...c,
    label: `${c.checklist_code} - ${c.title}`,
  }))
  const companies = incluirOpcao(empresas, form.company)
  const users = incluirOpcao(formInit.users, form.responsavelUser)

  const onBtnSalvarClicked = () => {
    const erro = validar(form, preenchimento)
    if (erro) return onError(erro)

    onSave({
      id: preenchimento ? preenchimento.id : undefined,
      checklist_id: form.checklist.id,
      company_id: form.company ? form.company.id : null,
      responsavel_user_id: form.semUsuario ? null : form.responsavelUser.id,
      responsavel_nome: form.semUsuario ? form.responsavelNome.trim() : null,
      deadline: form.comDeadline
        ? formatarDataHoraPayload(form.deadlineData, form.deadlineHora)
        : null,
      observacao: form.observacao.trim() || null,
    })
  }

  return (
    <CustomModal
      header={edicao ? 'Editar Preenchimento de Checklist' : 'Novo Preenchimento de Checklist'}
      isVisible={show}
      onClose={onClose}
      width="800px"
    >
      <div className="p-grid">
        <div className="p-col-12">
          <CustomDropdown
            label="Formulário:*"
            optionLabel="label"
            options={checklists}
            value={checklists.find(c => form.checklist && c.id === form.checklist.id) || null}
            onChange={value => onChange('checklist', value)}
            disabled={edicao}
            placeholder="Selecione o formulário"
            filter
          />
          {edicao ? (
            <div style={styles.dica}>
              O formulário não pode ser alterado após a criação do preenchimento.
            </div>
          ) : null}
        </div>

        <div className="p-col-12">
          <CustomDropdown
            label="Empresa:"
            optionLabel="name"
            options={companies}
            value={form.company}
            onChange={value => onChange('company', value)}
            placeholder="Selecione a empresa (opcional)"
            showClear
            filter
          />
        </div>

        <div className="p-md-7 p-col-12">
          {form.semUsuario ? (
            <InputText
              label="Nome do Responsável pelo preenchimento:*"
              marginTop={9}
              maxLength={LIMITES.responsavelNome}
              value={form.responsavelNome}
              onChange={value => onChange('responsavelNome', value)}
            />
          ) : (
            <CustomDropdown
              label="Responsável pelo preenchimento:*"
              optionLabel="name"
              options={users}
              value={form.responsavelUser}
              onChange={value => onChange('responsavelUser', value)}
              placeholder="Selecione o usuário"
              filter
            />
          )}
        </div>
        <div className="p-md-5 p-col-12" style={styles.checkbox}>
          <CustomCheckbox
            checked={form.semUsuario}
            onChange={value => onChange('semUsuario', value)}
          >
            Responsável não possui usuário cadastrado
          </CustomCheckbox>
        </div>

        <div className="p-md-5 p-col-12" style={styles.checkbox}>
          <CustomCheckbox
            checked={form.comDeadline}
            onChange={value => onChange('comDeadline', value)}
          >
            Definir Deadline
          </CustomCheckbox>
        </div>
        {form.comDeadline ? (
          <>
            <div className="p-md-4 p-col-12">
              <CustomCalendar
                label="Data:*"
                value={form.deadlineData}
                onChange={value => onChange('deadlineData', value)}
              />
            </div>
            <div className="p-md-3 p-col-12">
              <InputTime
                label="Hora:*"
                marginTop={10}
                value={form.deadlineHora}
                onChange={value => onChange('deadlineHora', value)}
              />
            </div>
          </>
        ) : (
          <div className="p-md-7 p-col-12" />
        )}
        {edicao && preenchimento.created_at ? (
          <div className="p-col-12" style={styles.dica}>
            Criado em {formatarDataHora(preenchimento.created_at)}. O deadline não pode ser anterior
            à criação.
          </div>
        ) : null}

        <div className="p-col-12">
          <CustomInputTextArea
            label="Observação"
            rows={3}
            value={form.observacao}
            onChange={value => onChange('observacao', value)}
          />
        </div>

        <div className="p-col-12">
          <Btn onClick={onBtnSalvarClicked}>Salvar</Btn>
        </div>
      </div>
    </CustomModal>
  )
}
